import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Truck, Package, Search, Mail, Phone } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtNum, fmtDateTime, norm } from "@/lib/format";

const TABS = [
  { key: "pendiente", label: "Pendientes" },
  { key: "aprobado", label: "Aprobados" },
  { key: "entregado", label: "Entregados" },
  { key: "rechazado", label: "Rechazados" },
];

export default function AdminRedemptions() {
  usePageTitle("Canjes");
  const [reds, setReds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "pendiente";
  const q = params.get("q") || "";
  const [confirm, setConfirm] = useState<{ red: any; decision: "rechazado" | "entregado" } | null>(null);
  const [reason, setReason] = useState("");

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const load = () => {
    setFailed(false);
    return api.get("/admin/redemptions").then((r) => setReds(r.data)).catch(() => setFailed(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const decide = async (red: any, decision: string, why?: string) => {
    try {
      await api.post(`/admin/redemptions/${red.id}/decision`, { decision, reason: why || null });
      const msg: Record<string, string> = {
        aprobado: `Canje aprobado. ${red.user_name} recibirá una notificación.`,
        rechazado: `Canje rechazado. Se devolvieron ${fmtNum(red.points_spent)} puntos a ${red.user_name}.`,
        entregado: `Canje marcado como entregado. ${red.user_name} recibirá una notificación.`,
      };
      toast.success(msg[decision]);
      load();
    } catch (err: any) {
      toast.error(apiError(err));
      throw err;
    }
  };

  const filtered = reds.filter(
    (r) => r.status === tab && (!q || [r.user_name, r.reward_name, r.user_email, r.user_cedula].some((v) => norm(v).includes(norm(q))))
  );

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-redemptions-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-medium">Canjes</h1>
          <p className="text-sm text-slate-600 mt-1">Solicitudes de premios de los clientes: apruébalas, recházalas o márcalas como entregadas.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <Input
            data-testid="redemption-search-input"
            aria-label="Buscar canjes"
            value={q}
            onChange={(e: any) => setParam("q", e.target.value)}
            placeholder="Buscar por cliente, cédula o premio"
            className="pl-9 bg-obsidian-card border-slate-200 text-slate-900 h-11"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const count = reds.filter((r) => r.status === t.key).length;
          return (
            <button
              key={t.key}
              data-testid={`redemption-tab-${t.key}`}
              onClick={() => setParam("tab", t.key === "pendiente" ? "" : t.key)}
              aria-pressed={tab === t.key}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                tab === t.key ? "gold-gradient-bg text-white font-semibold border-transparent" : "border-slate-200 text-slate-600 hover:text-slate-900"
              }`}
            >
              {t.label} {count > 0 && `(${count})`}
            </button>
          );
        })}
      </div>

      {failed ? (
        <LoadError what="los canjes" onRetry={load} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Package}>
          {q ? <>No hay canjes que coincidan con “{q}”.</> : tab === "pendiente" ? "No hay solicitudes pendientes. Cuando un cliente pida un premio, aparecerá aquí." : "No hay canjes en esta pestaña."}
        </EmptyState>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => (
            <div key={r.id} data-testid="admin-redemption-item" className="card-obsidian p-4 flex flex-wrap items-center gap-4">
              <img src={r.reward_image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
              <div className="flex-1 min-w-[12rem]">
                <h2 className="font-semibold text-slate-900">{r.reward_name}</h2>
                <p className="text-sm text-slate-700">{r.user_name}{r.user_cedula ? ` · Cédula ${r.user_cedula}` : ""}</p>
                <p className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-3">
                  {r.user_email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" aria-hidden="true" />{r.user_email}</span>}
                  {r.user_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" aria-hidden="true" />{r.user_phone}</span>}
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Solicitado el {fmtDateTime(r.created_at)} · {fmtNum(r.points_spent)} pts
                </p>
              </div>
              <div className="flex items-center gap-2">
                {r.status === "pendiente" && (
                  <>
                    <Button data-testid="admin-approve-redemption-button" size="sm" onClick={() => decide(r, "aprobado").catch(() => {})} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
                      <Check className="w-4 h-4 mr-1" aria-hidden="true" /> Aprobar
                    </Button>
                    <Button data-testid="admin-reject-redemption-button" size="sm" variant="outline" onClick={() => { setReason(""); setConfirm({ red: r, decision: "rechazado" }); }} className="border-red-500/30 text-red-700 hover:bg-red-500/10 rounded-full">
                      <X className="w-4 h-4 mr-1" aria-hidden="true" /> Rechazar
                    </Button>
                  </>
                )}
                {r.status === "aprobado" && (
                  <>
                    <Button data-testid="admin-deliver-redemption-button" size="sm" onClick={() => setConfirm({ red: r, decision: "entregado" })} className="bg-navy hover:bg-navy-dark text-white rounded-full">
                      <Truck className="w-4 h-4 mr-1" aria-hidden="true" /> Marcar entregado
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setReason(""); setConfirm({ red: r, decision: "rechazado" }); }} className="border-red-500/30 text-red-700 hover:bg-red-500/10 rounded-full">
                      <X className="w-4 h-4 mr-1" aria-hidden="true" /> Rechazar
                    </Button>
                  </>
                )}
                {(r.status === "entregado" || r.status === "rechazado") && <StatusBadge status={r.status} />}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirm && confirm.decision === "rechazado"}
        onOpenChange={(o) => !o && setConfirm(null)}
        testid="reject-redemption-dialog"
        danger
        title={`¿Rechazar el canje de ${confirm?.red.user_name || ""}?`}
        description={<>
          <p>Se devolverán <strong>{fmtNum(confirm?.red.points_spent)} puntos</strong> a {confirm?.red.user_name} y el premio “{confirm?.red.reward_name}” volverá a estar disponible.</p>
          <p>El cliente recibirá una notificación. Esta decisión no se puede deshacer.</p>
        </>}
        confirmLabel="Rechazar y devolver puntos"
        onConfirm={() => decide(confirm!.red, "rechazado", reason.trim())}
      >
        <div>
          <Label htmlFor="redemption-reason" className="text-slate-700 text-sm">Motivo (opcional, lo verá el cliente)</Label>
          <Textarea id="redemption-reason" data-testid="redemption-reason-input" rows={3} value={reason} onChange={(e: any) => setReason(e.target.value)} placeholder="Ej: El premio se agotó en tienda. Puedes elegir otro del catálogo." className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!confirm && confirm.decision === "entregado"}
        onOpenChange={(o) => !o && setConfirm(null)}
        testid="deliver-redemption-dialog"
        title="¿Marcar como entregado?"
        description={<p>Confirma que {confirm?.red.user_name} ya recibió “{confirm?.red.reward_name}”. Después no se podrá rechazar ni cambiar.</p>}
        confirmLabel="Sí, ya se entregó"
        onConfirm={() => decide(confirm!.red, "entregado")}
      />
    </div>
  );
}
